import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2, MoreVertical, UserPlus, Users, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import { type Column, DataTable } from "../components/DataTable";
import {
  ApiError,
  type CustomRole,
  type Role,
  type UserListItem,
  customRolesApi,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/team/")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin" && user.role !== "manager") {
      throw redirect({ to: "/deals" });
    }
  },
  component: TeamIndexPage,
});

const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  manager: "Manager",
  production: "Production",
  sales: "Sales",
};

const ROLE_DESCRIPTION: Record<Role, string> = {
  admin: "Company administration and all CRM/ERP operations",
  manager: "All operations except employee administration and record deletion",
  production: "Production-safe orders, products, raw materials, and inventory",
  sales: "CRM sales work assigned or shared with this employee",
};

const ROLE_BADGE: Record<Role, string> = {
  admin: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  production: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  manager: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  sales: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
};

function TeamIndexPage() {
  const actor = useAuthStore((state) => state.user);
  const isAdmin = actor?.role === "admin";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [includeInactive, setIncludeInactive] = useState(isAdmin);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [deactivating, setDeactivating] = useState<UserListItem | null>(null);
  const [transferring, setTransferring] = useState<UserListItem | null>(null);

  const listQuery = useQuery({
    queryKey: ["team:list", { includeInactive }],
    queryFn: () => usersApi.list({ active_only: !includeInactive }),
  });
  const customRolesQuery = useQuery({
    queryKey: ["custom-roles"],
    queryFn: customRolesApi.list,
    enabled: isAdmin,
  });
  const customRoles = customRolesQuery.data?.items.filter((role) => role.is_active) ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["team:list"] });

  const reactivate = useMutation({
    mutationFn: (id: string) => usersApi.reactivate(id),
    onSuccess: () => {
      toast.success("Employee account reactivated.");
      refresh();
    },
    onError: (cause) => toast.error(errorMessage(cause, "Could not reactivate employee.")),
  });
  const resend = useMutation({
    mutationFn: (id: string) => usersApi.resendInvite(id),
    onSuccess: () => toast.success("A new verification code was emailed."),
    onError: (cause) => toast.error(errorMessage(cause, "Could not resend invitation.")),
  });

  const columns: Column<UserListItem>[] = [
    {
      header: "Employee",
      cell: (employee) => (
        <div className="min-w-0">
          <div className="truncate font-medium">
            {employee.name || "Name pending"}
            {employee.is_owner && (
              <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                Owner
              </span>
            )}
          </div>
          <div className="truncate text-xs opacity-60">{employee.email}</div>
        </div>
      ),
    },
    {
      header: "Company role",
      width: "w-40",
      cell: (employee) => (
        <span
          className={`inline-block rounded-full px-2 py-1 text-[11px] font-medium uppercase tracking-wide ${ROLE_BADGE[employee.role]}`}
        >
          {customRoles.find((role) => role.id === employee.custom_role_id)?.name ??
            ROLE_LABEL[employee.role]}
        </span>
      ),
    },
    {
      header: "Account",
      width: "w-36",
      cell: (employee) => <AccountStatus employee={employee} />,
    },
    {
      header: "Last sign-in",
      width: "w-40",
      hideOnSmall: true,
      cell: (employee) => (
        <span className="text-xs opacity-70">
          {employee.last_login_at ? new Date(employee.last_login_at).toLocaleDateString() : "Never"}
        </span>
      ),
    },
    ...(isAdmin
      ? [
          {
            header: "Actions",
            width: "w-20",
            align: "right" as const,
            cell: (employee: UserListItem) => (
              <div className="relative z-10 inline-block">
                <button
                  type="button"
                  className="flex size-11 items-center justify-center rounded-md hover:bg-[var(--hover)]"
                  aria-label={`Actions for ${employee.name || employee.email}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuFor(menuFor === employee.id ? null : employee.id);
                  }}
                >
                  <MoreVertical className="size-4" aria-hidden />
                </button>
                {menuFor === employee.id && (
                  <div className="absolute right-0 top-10 z-30 w-52 overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface-elevated)] py-1 text-left shadow-xl">
                    <MenuButton
                      onClick={() =>
                        navigate({ to: "/team/$userId", params: { userId: employee.id } })
                      }
                    >
                      View performance
                    </MenuButton>
                    <MenuButton
                      onClick={() => {
                        setEditing(employee);
                        setMenuFor(null);
                      }}
                    >
                      Modify role/access
                    </MenuButton>
                    {!employee.invite_accepted_at && employee.is_active && (
                      <MenuButton
                        onClick={() => {
                          resend.mutate(employee.id);
                          setMenuFor(null);
                        }}
                      >
                        Resend verification code
                      </MenuButton>
                    )}
                    {actor?.is_owner &&
                      employee.role === "admin" &&
                      !employee.custom_role_id &&
                      employee.is_active &&
                      employee.email_verified_at &&
                      !employee.is_owner && (
                        <MenuButton
                          onClick={() => {
                            setTransferring(employee);
                            setMenuFor(null);
                          }}
                        >
                          Transfer ownership
                        </MenuButton>
                      )}
                    {employee.is_active ? (
                      <MenuButton
                        danger
                        disabled={employee.is_owner || employee.id === actor?.id}
                        onClick={() => {
                          setDeactivating(employee);
                          setMenuFor(null);
                        }}
                      >
                        Deactivate account
                      </MenuButton>
                    ) : (
                      <MenuButton
                        onClick={() => {
                          reactivate.mutate(employee.id);
                          setMenuFor(null);
                        }}
                      >
                        Reactivate account
                      </MenuButton>
                    )}
                  </div>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Users className="size-5 opacity-70" aria-hidden />
            {isAdmin ? "Employees" : "Team"}
          </h1>
          <p className="text-sm opacity-60">
            {isAdmin
              ? "Manage employee identities, access, account state, and performance."
              : "See what each teammate is working on."}
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white"
          >
            <UserPlus className="size-4" aria-hidden />
            Add employee
          </button>
        )}
      </header>
      {isAdmin && (
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Include deactivated accounts
        </label>
      )}
      {listQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" />
          Loading employees…
        </p>
      ) : listQuery.error ? (
        <div role="alert" className="text-sm text-red-600">
          {(listQuery.error as Error).message}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={listQuery.data?.items ?? []}
          rowKey={(employee) => employee.id}
          rowHref={(employee) => `/team/${employee.id}`}
          empty={
            <div className="rounded-xl border border-dashed border-[var(--border)] p-10 text-center text-sm opacity-60">
              No employees to show.
            </div>
          }
        />
      )}
      {creating && (
        <CreateEmployeeDialog
          customRoles={customRoles}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <RoleDialog
          employee={editing}
          customRoles={customRoles}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
      {deactivating && (
        <DeactivateDialog
          employee={deactivating}
          onClose={() => setDeactivating(null)}
          onSaved={() => {
            setDeactivating(null);
            refresh();
          }}
        />
      )}
      {transferring && (
        <TransferOwnershipDialog
          employee={transferring}
          onClose={() => setTransferring(null)}
          onSaved={async () => {
            setTransferring(null);
            await useAuthStore.getState().refresh();
            refresh();
          }}
        />
      )}
    </div>
  );
}

function AccountStatus({ employee }: { employee: UserListItem }) {
  if (!employee.is_active)
    return <span className="text-xs font-medium text-red-600">Deactivated</span>;
  if (!employee.invite_accepted_at)
    return <span className="text-xs font-medium text-amber-600">Awaiting verification</span>;
  return <span className="text-xs font-medium text-emerald-600">Active</span>;
}

function MenuButton({
  children,
  onClick,
  danger = false,
  disabled = false,
}: { children: React.ReactNode; onClick: () => void; danger?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`block min-h-11 w-full px-3 py-2 text-left text-sm hover:bg-[var(--hover)] disabled:cursor-not-allowed disabled:opacity-40 ${danger ? "text-red-600" : ""}`}
    >
      {children}
    </button>
  );
}

function DialogShell({
  title,
  children,
  onClose,
}: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="presentation"
    >
      <dialog
        open
        aria-modal="true"
        aria-label={title}
        className="relative m-0 w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--surface-elevated)] p-5 text-inherit shadow-2xl"
      >
        <header className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex size-11 items-center justify-center rounded-md"
          >
            <X className="size-4" />
          </button>
        </header>
        {children}
      </dialog>
    </div>
  );
}

function CreateEmployeeDialog({
  customRoles,
  onClose,
  onSaved,
}: { customRoles: CustomRole[]; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("sales");
  const [customRoleId, setCustomRoleId] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      usersApi.create({ email, name: name || null, role, custom_role_id: customRoleId }),
    onSuccess: () => {
      toast.success("Employee created and verification code emailed.");
      onSaved();
    },
    onError: (cause) => toast.error(errorMessage(cause, "Could not create employee.")),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  return (
    <DialogShell title="Add employee" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <label className="block text-sm">
          <span className="mb-1 block">Email</span>
          <input
            className="form-input w-full"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block">Name</span>
          <input
            className="form-input w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <RoleSelect
          role={role}
          customRoleId={customRoleId}
          customRoles={customRoles}
          onChange={(nextRole, nextCustomRoleId) => {
            setRole(nextRole);
            setCustomRoleId(nextCustomRoleId);
          }}
        />
        <p className="rounded-md bg-[var(--hover)] p-3 text-xs opacity-80">
          {customRoles.find((item) => item.id === customRoleId)?.description ??
            ROLE_DESCRIPTION[role]}
          . The employee will receive a 15-minute verification code and choose their own password.
        </p>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="min-h-11 w-full rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Create and email code
        </button>
      </form>
    </DialogShell>
  );
}

function RoleDialog({
  employee,
  customRoles,
  onClose,
  onSaved,
}: {
  employee: UserListItem;
  customRoles: CustomRole[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState(employee.role);
  const [customRoleId, setCustomRoleId] = useState<string | null>(employee.custom_role_id ?? null);
  const mutation = useMutation({
    mutationFn: () =>
      usersApi.update(
        employee.id,
        customRoleId ? { custom_role_id: customRoleId } : { role, custom_role_id: null },
      ),
    onSuccess: () => {
      toast.success("Employee access updated.");
      onSaved();
    },
    onError: (cause) => toast.error(errorMessage(cause, "Could not update access.")),
  });
  return (
    <DialogShell title="Modify role and access" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <div className="font-medium">{employee.name || employee.email}</div>
          <div className="text-xs opacity-60">{employee.email}</div>
        </div>
        <RoleSelect
          role={role}
          customRoleId={customRoleId}
          customRoles={customRoles}
          onChange={(nextRole, nextCustomRoleId) => {
            setRole(nextRole);
            setCustomRoleId(nextCustomRoleId);
          }}
          disabled={employee.is_owner}
        />
        <p className="rounded-md bg-[var(--hover)] p-3 text-xs opacity-80">
          {customRoles.find((item) => item.id === customRoleId)?.description ??
            ROLE_DESCRIPTION[role]}
        </p>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={
            mutation.isPending ||
            (role === employee.role && customRoleId === employee.custom_role_id)
          }
          className="min-h-11 w-full rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Save access
        </button>
      </div>
    </DialogShell>
  );
}

function RoleSelect({
  role,
  customRoleId,
  customRoles,
  onChange,
  disabled = false,
}: {
  role: Role;
  customRoleId: string | null;
  customRoles: CustomRole[];
  onChange: (role: Role, customRoleId: string | null) => void;
  disabled?: boolean;
}) {
  const value = customRoleId ? `custom:${customRoleId}` : role;
  return (
    <label className="block text-sm">
      <span className="mb-1 block">Company role</span>
      <select
        className="form-input w-full"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const selected = e.target.value;
          if (selected.startsWith("custom:")) {
            const custom = customRoles.find((item) => item.id === selected.slice(7));
            if (custom) onChange(custom.base_role, custom.id);
          } else onChange(selected as Role, null);
        }}
      >
        {(Object.keys(ROLE_LABEL) as Role[]).map((item) => (
          <option key={item} value={item}>
            {ROLE_LABEL[item]}
          </option>
        ))}
        {customRoles.length > 0 && (
          <optgroup label="Custom roles">
            {customRoles.map((item) => (
              <option key={item.id} value={`custom:${item.id}`}>
                {item.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}

function DeactivateDialog({
  employee,
  onClose,
  onSaved,
}: { employee: UserListItem; onClose: () => void; onSaved: () => void }) {
  const [secondWarning, setSecondWarning] = useState(false);
  const mutation = useMutation({
    mutationFn: () => usersApi.deactivate(employee.id),
    onSuccess: () => {
      toast.success("Employee account deactivated and sessions revoked.");
      onSaved();
    },
    onError: (cause) => toast.error(errorMessage(cause, "Could not deactivate employee.")),
  });
  return (
    <DialogShell title="Deactivate employee account" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm">
          {secondWarning ? (
            <>
              <strong>Final warning:</strong> {employee.name || employee.email} will immediately
              lose access on every device.
            </>
          ) : (
            <>
              This disables sign-in and revokes all existing sessions. Their historical work remains
              attributed to them.
            </>
          )}
        </p>
        {secondWarning ? (
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="min-h-11 w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white"
          >
            Yes, deactivate account
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setSecondWarning(true)}
            className="min-h-11 w-full rounded-md border border-red-400 px-4 py-2 text-sm font-medium text-red-600"
          >
            Continue
          </button>
        )}
      </div>
    </DialogShell>
  );
}

function TransferOwnershipDialog({
  employee,
  onClose,
  onSaved,
}: { employee: UserListItem; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [confirmed, setConfirmed] = useState(false);
  const mutation = useMutation({
    mutationFn: () => usersApi.transferOwnership(employee.id),
    onSuccess: async () => {
      toast.success("Company ownership transferred.");
      await onSaved();
    },
    onError: (cause) => toast.error(errorMessage(cause, "Could not transfer ownership.")),
  });
  return (
    <DialogShell title="Transfer company ownership" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm">
          {confirmed ? (
            <>
              <strong>Final warning:</strong> this immediately makes{" "}
              {employee.name || employee.email}
              the only Owner. You will remain an Admin but lose Owner-only integration and ownership
              controls.
            </>
          ) : (
            <>Transfer the protected Owner role to {employee.name || employee.email}?</>
          )}
        </p>
        <button
          type="button"
          onClick={() => (confirmed ? mutation.mutate() : setConfirmed(true))}
          disabled={mutation.isPending}
          className={`min-h-11 w-full rounded-md px-4 py-2 text-sm font-medium ${confirmed ? "bg-red-600 text-white" : "border border-red-400 text-red-600"}`}
        >
          {confirmed ? "Yes, transfer ownership" : "Continue"}
        </button>
      </div>
    </DialogShell>
  );
}

function errorMessage(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError)
    return cause.reauthRequired
      ? "For security, sign out and sign in again before changing employee access."
      : cause.message;
  return fallback;
}
